import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import {
  PageTemplate,
  Question,
  Questionnaire,
  QuestionnairePayload,
  QuestionnaireStats,
  QuestionnaireTemplate,
} from '../models/questionnaire.model';
import { ApiResponse, FilterParams, PageResponse, Result } from '../models/common.model';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  pipe,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints.constants';
import { environment } from '@environments/environment';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { QuestionResponseDto } from '../models/questionnaire-analytics.model';
import { CapturingResponseEntity } from '../models/capturing-response.model';

@Injectable({ providedIn: 'root' })
export class CoreService {
  constructor(private apiService: ApiService) {
    // Pre-load templates immediately when service is instantiated
    this.loadTemplates();
  }

  questionnaireDeatils: BehaviorSubject<Questionnaire> = new BehaviorSubject<Questionnaire>(
    null as any
  );
  questionnaireDeatils$ = this.questionnaireDeatils.asObservable();

  // Template cache for immediate access
  private templatesSubject: BehaviorSubject<QuestionnaireTemplate[]> = new BehaviorSubject<
    QuestionnaireTemplate[]
  >([]);
  templates$: Observable<QuestionnaireTemplate[]> = this.templatesSubject.asObservable();
  // private templatesLoaded: boolean = false;
  // private templatesLoading: boolean = false;

  setQuestionnaireDetails(questionnaire: Questionnaire) {
    this.questionnaireDeatils.next(questionnaire);
  }

  getQuestionnaireDetailsFromSubject(): Observable<Questionnaire> {
    return this.questionnaireDeatils$;
  }

  // create questionnaire api call - Completed
  createQuestionnaire(
    payload: QuestionnairePayload,
    orgId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .post<ApiResponse<Questionnaire>, QuestionnairePayload>(
        API_ENDPOINTS.QUESTIONNAIRE.BASE(orgId),
        payload
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire created successfully:', result.data);
          }
        }),
        catchError((error) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  publishQuestionnaire(
    payload: QuestionnairePayload,
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .put<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.PUBLISH(orgId, questionnaireId),
        payload
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire published successfully:', result.data);
          }
        }),
        catchError((error) => {
          console.log(error, ' error in catchError');
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }
  updateQuestionnaire(
    payload: QuestionnairePayload,
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .put<ApiResponse<Questionnaire>, QuestionnairePayload>(
        API_ENDPOINTS.QUESTIONNAIRE.BASE_ID(orgId, questionnaireId),
        payload
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire updated successfully:', result.data);
          }
        }),
        catchError((error) => {
          console.log(error, ' error in catchError');
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }
  getQuestionnaireList(
    orgId: string,
    params: FilterParams
  ): Observable<Result<PageResponse<Questionnaire>>> {
    return this.apiService
      .get<ApiResponse<PageResponse<Questionnaire>>>(API_ENDPOINTS.QUESTIONNAIRE.BASE(orgId), {
        params,
      })
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        tap((result) => {
          if (result.success) {
            console.log(' Questionnaires users list:', result.data.content);
          }
        }),
        catchError((error) => {
          if (environment.consoleFlag) console.error('Questionnaire list retrieval failed:', error);
          return of(this.apiService.handleApiError<PageResponse<Questionnaire>>(error));
        })
      );
  }
  deleteQuestionnaire(orgId: string, questionnaireId: string): Observable<Result<Questionnaire>> {
    return this.apiService
      .delete<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.BASE_ID(orgId, questionnaireId)
      )
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire deleted successfully:', result.data);
          }
        }),
        catchError((error) => {
          console.log(error, ' error in catchError');
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }
  closeQuestionnaire(orgId: string, questionnaireId: string, reason?: string): Observable<Result<Questionnaire>> {
    return this.apiService
    .put<ApiResponse<Questionnaire>>(
      API_ENDPOINTS.QUESTIONNAIRE.CLOSE(orgId, questionnaireId),
      reason?.trim() ? { reason: reason.trim() } : {}
    )
    .pipe(
      map((res) => ({ success: true as const, data: res.data })),
      tap((result) => {
        if (result.success) {
          console.log('Questionnaire Closed successfully:', result.data);
        }
      }),
      catchError((error) => {
        return throwError(() => this.apiService.handleApiError<QuestionnaireStats>(error));
      })
    );

      // .put<ApiResponse<Questionnaire>>(
      //   API_ENDPOINTS.QUESTIONNAIRE.CLOSE(orgId, questionnaireId)
      // )
      // .pipe(
      //   map((res) => ({ success: true as const, data: res.data })),
      //   tap((result) => {
      //     if (result.success) {
      //       console.log('✅ Questionnaire deleted successfully:', result.data);
      //     }
      //   }),
      //   catchError((error) => {
      //     console.log(error, ' error in catchError');
      //     return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
      //   })
      // );
  }
  cloneQuestionnaire(orgId: string, questionnaireId: string): Observable<Result<Questionnaire>> {
    return this.apiService
      .post<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.CLONE(orgId, questionnaireId),
        {}
      )
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire cloned successfully:', result.data);
          }
        }),
        catchError((error) => {
          return throwError(() => this.apiService.handleApiError<QuestionnaireStats>(error));
        })
      );
  }
  getQuestionnaireListCount(orgId: string): Observable<Result<QuestionnaireStats>> {
    return this.apiService
      .get<ApiResponse<QuestionnaireStats>>(API_ENDPOINTS.QUESTIONNAIRE.COUNT(orgId))
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        catchError((error) => {
          console.log(error, ' error in catchError');
          return of(this.apiService.handleApiError<QuestionnaireStats>(error));
        })
      );
  }
  //-exception handling  done
  getQuestionnaireDetails(
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .get<ApiResponse<Questionnaire>>(API_ENDPOINTS.QUESTIONNAIRE.BASE_ID(orgId, questionnaireId))
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        shareReplay(1),
        tap((result) => {
          if (result.success) {
            this.questionnaireDeatils.next(result.data);
            console.log('✅ Questionnaire details fetched successfully:', result.data);
            this.setQuestionnaireDetails(result.data);
          }
        }),
        catchError((error: any) => {
          // if (environment.consoleFlag) console.error('Questionnaire list retrieval failed:', error);
          return of(this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  createQuestion(payload: any, orgId: string, questionnaireId: string): Observable<Result<any>> {
    return this.apiService
      .post<ApiResponse<any>, any>(
        API_ENDPOINTS.QUESTIONNAIRE.QUESTIONS(orgId, questionnaireId),
        payload
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire created successfully:', result.data);
          }
        }),
        catchError((error) => {
          console.log(error, ' error in catchError');
          return throwError(() => this.apiService.handleApiError<any>(error));
        })
      );
  }
  updateQuestion(
    payload: any,
    orgId: string,
    questionnaireId: string,
    questionId: string
  ): Observable<Result<any>> {
    return this.apiService
      .put<ApiResponse<any>, any>(
        API_ENDPOINTS.QUESTIONNAIRE.QUESTIONS_ID(orgId, questionnaireId, questionId),
        payload
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        catchError((error) => {
          console.log(error, ' error in catchError');
          return throwError(() => this.apiService.handleApiError<any>(error));
        })
      );
  }
  getQuestionList(
    orgId: string,
    questionnaireId: string,
    params: FilterParams
  ): Observable<Result<Questionnaire[]>> {
    return this.apiService
      .get<ApiResponse<Questionnaire[]>>(
        API_ENDPOINTS.QUESTIONNAIRE.QUESTIONS(orgId, questionnaireId),
        { params }
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        catchError((error: any) => {
          return of(this.apiService.handleApiError<Questionnaire[]>(error));
        })
      );
  }

  deleteQuestion(
    orgId: string,
    questionnaireId: string,
    questionId: string
  ): Observable<Result<Question>> {
    return this.apiService
      .delete<ApiResponse<Question>>(
        API_ENDPOINTS.QUESTIONNAIRE.QUESTIONS_ID(orgId, questionnaireId, questionId)
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        catchError((error) => {
          console.log(error, ' error in catchError');
          return throwError(() => this.apiService.handleApiError<Question>(error));
        })
      );
  }

  moveQuestion(
    orgId: string,
    questionnaireId: string,
    questionId: string,
    index: number
  ): Observable<Result<Question>> {
    const httpParams = new HttpParams({ fromObject: { index: index + 1 } });
    return this.apiService
      .patch<ApiResponse<Question>, any>(
        API_ENDPOINTS.QUESTIONNAIRE.MOVE_QUESTION(orgId, questionnaireId, questionId),
        {},
        { params: httpParams }
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Question moved successfully:', result.data);
          }
        }),
        catchError((error) => {
          return throwError(() => this.apiService.handleApiError<Question>(error));
        })
      );
  }
  getQRBasedQuestionnaire(
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .get<ApiResponse<Questionnaire>>(API_ENDPOINTS.QUESTIONNAIRE.QR_BASED(orgId, questionnaireId))
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire survey link fetched successfully:', result.data);
          }
        }),
        catchError((error) => {
          // console.log(error, ' error in catchError');
          return of(this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }
  submitSummitResponse(payload: any): Observable<Result<any>> {
    return this.apiService
      .post<ApiResponse<any>, any>(API_ENDPOINTS.QUESTIONNAIRE.SUMMIT_RESPONSE, payload)
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Summit response submitted successfully:', result.data);
          }
        }),
        catchError((error) => {
          return throwError(() => this.apiService.handleApiError<any>(error));
        })
      );
  }

  getQuestionnaireOverview(orgId: string, questionnaireId: string): Observable<Result<any>> {
    return this.apiService
      .get<ApiResponse<any>>(
        API_ENDPOINTS.QUESTIONNAIRE.QUESTIONNAIRE_OVERVIEW(orgId, questionnaireId)
      )
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire overview fetched successfully:', result.data);
          }
        }),
        catchError((error) => {
          return of(this.apiService.handleApiError<any>(error));
        })
      );
  }

  getQuestionOverview(orgId: string, questionnaireId: string): Observable<Result<any>> {
    return this.apiService
      .get<ApiResponse<any>>(API_ENDPOINTS.QUESTIONNAIRE.QUESTION_OVERVIEW(orgId, questionnaireId))
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Question overview fetched successfully:', result.data);
          }
        }),
        catchError((error) => {
          return of(this.apiService.handleApiError<QuestionResponseDto>(error));
        })
      );
  }

  getQuestionnaireResponses(
    questionnaireId: string,
    filterParams: FilterParams
  ): Observable<Result<PageResponse<QuestionResponseDto>>> {
    return this.apiService
      .get<ApiResponse<PageResponse<QuestionResponseDto>>>(
        API_ENDPOINTS.QUESTIONNAIRE.QUESTIONNAIRE_RESPONSES(questionnaireId),
        { params: filterParams }
      )
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Questionnaire responses fetched successfully:', result.data);
          }
        }),
        catchError((error) => {
          return of(this.apiService.handleApiError<PageResponse<QuestionResponseDto>>(error));
        })
      );
  }

  getQuestionResponses(questionnaireId: string, questionId: string): Observable<Result<any>> {
    return this.apiService
      .get<ApiResponse<any>>(
        API_ENDPOINTS.QUESTIONNAIRE.QUESTION_RESPONSES(questionnaireId, questionId)
      )
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Question responses fetched successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          console.log(error, ' error in catchError');
          return of(this.apiService.handleApiError<any>(error));
        })
      );
  }

  updateWelcomeScreen(
    formData: FormData,
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .putFormDataWithoutHeaders<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.WELCOME_SCREEN(orgId, questionnaireId),
        formData
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            // this.questionnaireDeatils.next(result.data);
            // this.setQuestionnaireDetails(result.data);
            console.log('✅ Welcome screen updated successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  deleteWelcomeLogo(
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .delete<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.WELCOME_SCREEN_LOGO(orgId, questionnaireId)
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Welcome screen logo deleted successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }
  updateHeaderScreen(
    formData: FormData,
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .putFormDataWithoutHeaders<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.HEADER_SCREEN(orgId, questionnaireId),
        formData
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            // this.questionnaireDeatils.next(result.data);
            // this.setQuestionnaireDetails(result.data);
            console.log('✅ Header screen updated successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  deleteHeaderLogo(
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .delete<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.HEADER_SCREEN_LOGO(orgId, questionnaireId)
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Header screen logo deleted successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  updateFooterScreen(
    payload: any,
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .put<ApiResponse<Questionnaire>, any>(
        API_ENDPOINTS.QUESTIONNAIRE.FOOTER_SCREEN(orgId, questionnaireId),
        payload
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            // this.questionnaireDeatils.next(result.data);
            // this.setQuestionnaireDetails(result.data);
            console.log('✅ Footer screen updated successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }
  updatePageScreen(
    formData: FormData,
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .putFormDataWithoutHeaders<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.PAGE_SETUP(orgId, questionnaireId),
        formData
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            // this.questionnaireDeatils.next(result.data);
            // this.setQuestionnaireDetails(result.data);
            console.log('✅ Header screen updated successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }
  updatePageTemplate(
    pageTemplate: PageTemplate,
    orgId: string,
    questionnaireId: string
    ): Observable<Result<PageTemplate>> {
    return this.apiService
      .put<ApiResponse<PageTemplate>, PageTemplate>(
        API_ENDPOINTS.QUESTIONNAIRE.PAGE_TEMPLATE(orgId, questionnaireId),
        pageTemplate
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Page template updated successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  updateMainContent(
    formData: FormData,
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .putFormDataWithoutHeaders<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.MAIN_CONTENT(orgId, questionnaireId),
        formData
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Main content updated successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  updateExitScreen(
    formData: FormData,
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .putFormDataWithoutHeaders<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.EXIT_SCREEN(orgId, questionnaireId),
        formData
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Exit screen updated successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  deleteExitLogo(
    orgId: string,
    questionnaireId: string
  ): Observable<Result<Questionnaire>> {
    return this.apiService
      .delete<ApiResponse<Questionnaire>>(
        API_ENDPOINTS.QUESTIONNAIRE.EXIT_SCREEN_LOGO(orgId, questionnaireId)
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            console.log('✅ Exit screen logo deleted successfully:', result.data);
          }
        }),
        catchError((error: any) => {
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  applyTemplate(
    orgId: string,
    questionnaireId: string,
    templateId: string
  ): Observable<Result<Questionnaire>> {
    const payload = {
      brandingTemplateId: templateId,
    };
    return this.apiService
      .put<ApiResponse<Questionnaire>, any>(
        API_ENDPOINTS.QUESTIONNAIRE.APPLY_TEMPLATE(orgId, questionnaireId, templateId),
        payload
      )
      .pipe(
        map((res) => ({
          success: true as const,
          data: res.data,
        })),
        tap((result) => {
          if (result.success) {
            this.questionnaireDeatils.next(result.data);
            this.setQuestionnaireDetails(result.data);
            console.log('✅ Template applied successfully:', result.data);
          }
        }),
        catchError((error) => {
          console.log(error, ' error in catchError');
          return throwError(() => this.apiService.handleApiError<Questionnaire>(error));
        })
      );
  }

  private loadTemplates(): void {
    // this.templatesLoading = true;
    this.apiService
      .get<ApiResponse<QuestionnaireTemplate[]>>(API_ENDPOINTS.QUESTIONNAIRE.TEMPLATE_LIST)
      .pipe(
        map((res) => res.data || []),
        catchError((error) => {
          console.error('Failed to load templates:', error);
          return throwError(() => this.apiService.handleApiError<QuestionnaireTemplate[]>(error));
        })
      )
      .subscribe((templates) => {
        this.templatesSubject.next(templates);
      });
  }

  // getTemplateList(): Observable<Result<QuestionnaireTemplate[]>> {
  //   // If templates are already loaded, return immediately from cache
  //   if (this.templatesLoaded) {
  //     return of({ success: true as const, data: this.templatesSubject.value });
  //   }

  //   // Otherwise, ensure loading is triggered and return the cached observable
  //   if (!this.templatesLoading) {
  //     this.loadTemplates();
  //   }

  //   // Return cached observable that will emit when templates are loaded
  //   return this.templates$.pipe(
  //     map((templates) => ({ success: true as const, data: templates })),
  //     shareReplay(1)
  //   );
  // }

  CheckQuestionnaire(
    orgId: string,
    appId: string,
    groupId: string,
    eventId: string,
    questionnaireType: string
  ): Observable<Result<{ isExist: boolean }>> {
    const params = new HttpParams({ fromObject: { appId, groupId, eventId, questionnaireType } });
    return this.apiService
      .get<ApiResponse<{ isExist: boolean }>>(API_ENDPOINTS.QUESTIONNAIRE.CHECK_QUESTIONNAIRE(orgId), { params })
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        catchError((error) => {
          return throwError(() => this.apiService.handleApiError<{ isExist: boolean }>(error));
        })
      );
  }
}
